import { normalizeName } from './shopping.ts';

/** What catalog search needs of an entry. */
export interface SearchableIngredient {
  id: string;
  nameDe: string;
  nameEn: string;
  aliases: readonly string[];
}

/**
 * Offline catalog search (quick add, staples): an exact name or alias first, then names and
 * aliases starting with the query, then words starting with it, then anything containing it.
 * Case and accents don't matter ("zwiebel" finds "Zwiebel", "creme" finds "Crème fraîche").
 */
export function searchCatalog<T extends SearchableIngredient>(
  entries: readonly T[],
  query: string,
  limit = 5,
): T[] {
  const q = normalizeName(query);
  if (!q) return [];
  // Ties go to the shortest matching name: "oat" → Oats before Oat milk.
  const scored: { entry: T; score: number; name: string }[] = [];
  for (const entry of entries) {
    const names = [entry.nameDe, entry.nameEn, ...entry.aliases].map(normalizeName);
    let score = 0;
    let best = '';
    for (const name of names) {
      const s =
        name === q
          ? 4
          : name.startsWith(q)
            ? 3
            : name.split(/[\s-]/).some((w) => w.startsWith(q))
              ? 2
              : name.includes(q)
                ? 1
                : 0;
      if (s > score || (s === score && s > 0 && name.length < best.length)) {
        score = s;
        best = name;
      }
    }
    if (score > 0) scored.push({ entry, score, name: best });
  }
  return scored
    .sort(
      (a, b) => b.score - a.score || a.name.length - b.name.length || a.name.localeCompare(b.name),
    )
    .slice(0, limit)
    .map((s) => s.entry);
}

/** The entry `query` names exactly (name or alias), if any. */
export function exactCatalogMatch<T extends SearchableIngredient>(
  entries: readonly T[],
  query: string,
): T | undefined {
  const q = normalizeName(query);
  return entries.find((e) =>
    [e.nameDe, e.nameEn, ...e.aliases].some((name) => normalizeName(name) === q),
  );
}
